'use client'

import type { MediaGalleryFieldsFragment } from '@/graphql/types'
import { useContentfulEntryLivePreview } from '@/components/providers/useContentfulEntryLivePreview'
import { DynamicMediaGallery } from '@ccpgames/eve-web-core'
import { mapMediaBlocksToItems } from '../utils'
import { useDlpTheme } from '@/components/providers/DlpThemeProvider'
import { toLibraryDlpTheme } from '@/lib/dlp-theme'
import i18nConfig from '@/i18n/i18nConfig'
import { useCurrentLocale } from 'next-i18n-router/client'
import { renderLink } from '@/utils/render-link'
import { buildContentfulInspectorProps } from '@/components/providers/contentfulInspectorProps'

interface MediaGalleryGridProps {
  entry: MediaGalleryFieldsFragment
}

/**
 * Grid view: Displays media items in a responsive grid layout
 * Uses DynamicMediaGallery component for consistent rendering
 */
export function MediaGalleryGrid({ entry }: MediaGalleryGridProps) {
  const { updatedEntry, inspectorProps } = useContentfulEntryLivePreview(entry)
  const pageTheme = useDlpTheme()
  const locale = useCurrentLocale(i18nConfig)

  const theme = toLibraryDlpTheme(pageTheme)

  const view = 'grid' as const

  const items = mapMediaBlocksToItems(
    updatedEntry.contentCollection,
    {
      view,
      fill: false,
      getItemSectionInspectorProps: (mediaBlock) => buildContentfulInspectorProps(mediaBlock.sys.id, 'media'),
    },
    locale,
  )

  if (items.length === 0) {
    return null
  }

  return (
    <DynamicMediaGallery
      view={view}
      title={updatedEntry.title ?? undefined}
      content={updatedEntry.subtitle ?? undefined}
      items={items}
      theme={theme}
      renderLink={renderLink}
      sectionInspectorProps={inspectorProps('contentCollection')}
    />
  )
}
